import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, BellRing, X } from 'lucide-react';
import { api, API_BASE } from '../lib/api';
import { useAuth } from '../lib/auth';
import { BRAND } from '@dru/types';
import { NOTIFICATIONS_CHANGED, notificationTarget } from '../lib/notificationCategories';

/**
 * The bell, and what arrives while somebody is working.
 *
 * Live: the API streams each new in-app notification the moment it is saved
 * (`/notifications/stream`, Server-Sent Events, read with fetch so the
 * session's token rides along). The inbox is still re-read on navigation,
 * when the tab comes back into view and every few minutes, so a dropped
 * connection costs nothing but speed. A notification that was not there on the previous
 * read pops over the page; one that was already waiting when the page loaded
 * does not — the badge says those exist, and popping twenty of them at sign-in
 * is noise, not news.
 *
 * With the tab in the background and the browser's permission given, the same
 * arrival is also a desktop notification. That one carries the subject only:
 * the subjects are written to be safe out loud ("Critical result requires
 * review"), while the body may name what happened and belongs behind the
 * sign-in, the same rule the email and SMS templates follow. The desktop
 * notification is tagged with the row's id, so two open tabs raise one.
 */

interface InboxItem {
  id: string;
  subject: string | null;
  body: string;
  linkPath: string | null;
  createdAt: string;
}

export { NOTIFICATIONS_CHANGED };

const holdsPermission = (perms: ReadonlySet<string>, p: string) =>
  perms.has(p) || perms.has(`${p.split('.')[0]}.*`) || perms.has('*');

/**
 * What each HR page needs. A notification's link is written for the company,
 * not for the reader: a "New invoice" reaches a login without billing, and
 * opening it would land on a refusal. Pages not listed are open to anybody
 * in the console (the page itself decides what to show).
 */
const HR_PAGE_NEEDS: Array<[string, string]> = [
  ['/hr/employees', 'employee.read'],
  ['/hr/examinations', 'examination.read'],
  ['/hr/appointments', 'appointment.read'],
  ['/hr/certificates', 'certificate.read'],
  ['/hr/campaigns', 'campaign.read'],
  ['/hr/treatments', 'treatment.read'],
  ['/hr/compliance', 'compliance.read'],
  ['/hr/dashboard', 'compliance.read'],
  ['/hr/billing', 'invoice.read'],
  ['/hr/users', 'user.create'],
  ['/hr/settings', 'settings.read'],
];

/** A notification link, or null when this login may not open the page it points at. */
export function permittedLink(to: string | null, permissions: ReadonlySet<string>): string | null {
  if (!to) return null;
  const need = HR_PAGE_NEEDS.find(([base]) => to === base || to.startsWith(`${base}/`) || to.startsWith(`${base}?`));
  return need && !holdsPermission(permissions, need[1]) ? null : to;
}

/** The fallback re-read, for when the live connection is down. */
const POLL_MS = 120_000;
const SHOWN_MS = 10_000;
const MAX_SHOWN = 3;

type Permission = NotificationPermission | 'unsupported';
const desktopPermission = (): Permission =>
  typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported';

export function NotificationCenter({ inbox, prefixes }: {
  /** The console's notification page, when it has one. */
  inbox: string | null;
  /** Route prefixes this session can open; a link elsewhere is not offered. */
  prefixes: string[];
}) {
  const { pathname } = useLocation();
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState<number | null>(null);
  const [shown, setShown] = useState<InboxItem[]>([]);
  const [permission, setPermission] = useState<Permission>(desktopPermission);
  /** Null until the first read: everything waiting then counts as already seen. */
  const seen = useRef<Set<string> | null>(null);
  const timers = useRef(new Map<string, number>());

  const here = `/${pathname.split('/')[1] ?? ''}`;
  const target = useCallback((item: InboxItem) =>
    permittedLink(notificationTarget(item, here, permissions, prefixes), permissions) ?? inbox,
  [here, inbox, permissions, prefixes]);

  const dismiss = useCallback((id: string) => {
    setShown((s) => s.filter((i) => i.id !== id));
    const t = timers.current.get(id);
    if (t) { window.clearTimeout(t); timers.current.delete(id); }
  }, []);

  const hold = useCallback((id: string) => {
    const t = timers.current.get(id);
    if (t) { window.clearTimeout(t); timers.current.delete(id); }
  }, []);

  const release = useCallback((id: string) => {
    hold(id);
    timers.current.set(id, window.setTimeout(() => dismiss(id), SHOWN_MS));
  }, [dismiss, hold]);

  const open = useCallback((item: InboxItem) => {
    dismiss(item.id);
    // Reading it here is reading it; the inbox would otherwise still call it new.
    // A read-only impersonation refuses the write, which changes nothing here.
    void api.post(`/notifications/${item.id}/read`, {})
      .then(() => { setUnread((u) => (u ? u - 1 : u)); window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED)); })
      .catch(() => undefined);
    const to = target(item);
    if (to) navigate(to);
  }, [dismiss, navigate, target]);

  const arrive = useCallback((fresh: InboxItem[]) => {
    window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED));
    setShown((s) => [...fresh.slice(0, MAX_SHOWN), ...s].slice(0, MAX_SHOWN));
    for (const item of fresh) release(item.id);
    if (desktopPermission() !== 'granted' || document.visibilityState === 'visible') return;
    for (const item of fresh.slice(0, MAX_SHOWN)) {
      try {
        const n = new Notification(item.subject ?? BRAND.name, {
          body: `Open ${BRAND.name} to read it.`,
          tag: item.id,
          icon: '/mark.png',
        });
        n.onclick = () => { window.focus(); open(item); n.close(); };
      } catch {
        // Some browsers only allow notifications from a service worker; the
        // in-page card above is still there when the tab is opened.
      }
    }
  }, [open, release]);

  const read = useCallback(async () => {
    try {
      const [count, items] = await Promise.all([
        api.get<{ unread: number }>('/notifications/inbox/unread-count'),
        api.get<InboxItem[]>('/notifications/inbox?unreadOnly=true&limit=10'),
      ]);
      setUnread(count.unread);
      const list = Array.isArray(items) ? items : [];
      if (!seen.current) { seen.current = new Set(list.map((i) => i.id)); return; }
      const fresh = list.filter((i) => !seen.current!.has(i.id));
      for (const i of fresh) seen.current.add(i.id);
      if (fresh.length) arrive(fresh);
    } catch {
      // A failed read leaves the last count standing; the next one tries again.
    }
  }, [arrive]);

  // On navigation, as the bell always did.
  useEffect(() => { void read(); }, [pathname, read]);

  // The notifications page marks things read or archived; the badge follows
  // at once instead of at the next navigation. Only the count is re-read:
  // the bell's own announcements of arrivals come through here too.
  useEffect(() => {
    const recount = () => {
      void api.get<{ unread: number }>('/notifications/inbox/unread-count')
        .then((c) => setUnread(c.unread)).catch(() => undefined);
    };
    window.addEventListener(NOTIFICATIONS_CHANGED, recount);
    return () => window.removeEventListener(NOTIFICATIONS_CHANGED, recount);
  }, []);

  useEffect(() => {
    const t = window.setInterval(() => { void read(); }, POLL_MS);
    const back = () => { if (document.visibilityState === 'visible') void read(); };
    document.addEventListener('visibilitychange', back);
    window.addEventListener('focus', back);
    return () => {
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', back);
      window.removeEventListener('focus', back);
    };
  }, [read]);

  useEffect(() => () => { for (const t of timers.current.values()) window.clearTimeout(t); }, []);

  // The stream outlives navigation: it reaches the latest handlers through
  // refs instead of reconnecting each time they change.
  const arriveRef = useRef(arrive);
  const readRef = useRef(read);
  arriveRef.current = arrive;
  readRef.current = read;

  // The live connection. Reconnects with a growing pause after a drop; on a
  // refused token it first makes an ordinary request, which refreshes it.
  useEffect(() => {
    let stopped = false;
    let attempt = 0;
    let ctrl: AbortController | null = null;
    let retry: number | undefined;

    const take = (raw: string) => {
      let event = 'message';
      let data = '';
      for (const line of raw.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (event !== 'notification' || !data) return;
      try {
        const item = JSON.parse(data) as InboxItem;
        if (!seen.current) seen.current = new Set();
        if (seen.current.has(item.id)) return;
        seen.current.add(item.id);
        setUnread((u) => (u ?? 0) + 1);
        arriveRef.current([item]);
      } catch { /* a malformed line is skipped */ }
    };

    const connect = async () => {
      if (stopped) return;
      ctrl = new AbortController();
      let refused = false;
      try {
        const token = api.getToken();
        const res = await fetch(`${API_BASE}/notifications/stream`, {
          headers: { accept: 'text/event-stream', ...(token ? { authorization: `Bearer ${token}` } : {}) },
          credentials: 'include',
          signal: ctrl.signal,
        });
        if (res.status === 401) refused = true;
        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
        attempt = 0;
        const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
        let buf = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += value;
          let cut: number;
          while ((cut = buf.indexOf('\n\n')) >= 0) {
            take(buf.slice(0, cut));
            buf = buf.slice(cut + 2);
          }
        }
      } catch {
        // Dropped, refused or aborted; the schedule below decides what next.
      }
      if (stopped) return;
      if (refused) await api.get('/notifications/inbox/unread-count').catch(() => undefined);
      attempt += 1;
      const wait = Math.min(2_000 * 1.5 ** attempt, 60_000);
      retry = window.setTimeout(() => { void readRef.current(); void connect(); }, wait);
    };

    void connect();
    return () => {
      stopped = true;
      ctrl?.abort();
      if (retry) window.clearTimeout(retry);
    };
  }, []);

  const askDesktop = () => {
    if (desktopPermission() !== 'default') return;
    void Notification.requestPermission().then(setPermission).catch(() => undefined);
  };

  const label = unread ? `Notifications, ${unread} unread` : 'Notifications';
  return (
    <>
      {inbox && (
        <Link
          to={inbox}
          aria-label={label}
          title={label}
          className="relative inline-flex h-9 w-9 max-md:h-10 max-md:w-10 items-center justify-center rounded-btn text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
        >
          <Bell className="h-[18px] w-[18px]" />
          {!!unread && (
            <span
              aria-hidden
              className="absolute right-1 top-1 min-w-[16px] rounded-full bg-primary-600 px-1 text-center text-micro leading-4 text-white"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Link>
      )}
      <div
        role="status"
        aria-live="polite"
        aria-label="New notifications"
        className="pointer-events-none fixed right-4 top-16 z-50 flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col gap-2"
      >
        {shown.map((item) => {
          const to = target(item);
          return (
            <div
              key={item.id}
              onMouseEnter={() => hold(item.id)}
              onMouseLeave={() => release(item.id)}
              className="pointer-events-auto rounded-card border border-neutral-200 bg-white p-3.5 shadow-dropdown animate-toast-in"
            >
              <div className="flex items-start gap-2.5">
                <span aria-hidden className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                  <BellRing className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-body font-medium text-neutral-900">{item.subject ?? 'New notification'}</p>
                  <p className="mt-0.5 line-clamp-2 text-caption font-normal text-neutral-600">{item.body}</p>
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(item.id)}
                  aria-label="Dismiss"
                  className="shrink-0 rounded p-0.5 text-neutral-400 hover:text-neutral-700"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="mt-2.5 flex items-center justify-end gap-3 pl-9">
                {permission === 'default' && (
                  <button type="button" onClick={askDesktop} className="mr-auto text-caption font-medium text-neutral-500 hover:text-neutral-800">
                    Also show on desktop
                  </button>
                )}
                {to && (
                  <button type="button" onClick={() => open(item)} className="text-caption font-semibold text-primary-600 hover:text-primary-700">
                    Open
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
